import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// Assuming 'api' is an instance of Axios or similar HTTP client
import api from '../../utils/api';
import ConceptDetailsModal from './ConceptDetailsModal';
import Layout from "../layout/DefaultLayout";
import { ConceptCreationModal } from './ConceptCreationModal';

// --- CONFIGURATION AND UTILITIES ---
const ItemTypes = {
    CONCEPT: 'concept',
};

// --- API Service REVISED ---
// Includes all necessary endpoints, with new ones for Concept Details
export const apiService = {
    getAllClassStandards: () =>
        api.get(`/class-standards`, { params: { isActive: true, sort: 'name,asc' } }),

    getAllSubjects: () =>
        api.get(`/subjects`, { params: { isActive: true, sort: 'name,asc' } }),

    getAllChapters: () =>
        api.get(`/chapters`, {
            params: {
                sort: 'name,asc',
                isActive: true,
            },
        }),

    getAllConcepts: () =>
        api.get(`/concepts`, { params: { isActive: true } }),

    getAllConceptChapters: () =>
        api.get(`/concept-chapters`, {
            params: {
                isActive: true
            }
        }),

    createConcept: (newConceptData) =>
        api.post(`/concepts`, newConceptData),

    linkConceptChapter: (chapterId, conceptId) =>
        api.post(`/concept-chapters`, {
            chapter: { id: chapterId },
            concept: { id: conceptId },
        }),

    unlinkConceptChapter: (conceptChapterId) =>
        api.delete(`/concept-chapters/${conceptChapterId}`),

    // NEW: API calls for Concept Details Modal
    getConceptUnits: (conceptId) =>
        api.get(`/concept-units`, { params: { 'conceptId.equals': conceptId, isActive: true } }),

    getUnitFacts: (unitId) =>
        api.get(`/concept-facts`, { params: { 'unitId.equals': unitId, isActive: true } }),

    getUnitAssessments: (unitId) =>
        api.get(`/concept-assessments`, { params: { 'unitId.equals': unitId, isActive: true } }),

    getUnitMaterials: (unitId) =>
        api.get(`/concept-materials`, { params: { 'unitId.equals': unitId, isActive: true } }),
};

// --- 2. Draggable Item Component ---
// --- 2. Draggable Item Component (FIXED) ---
const DraggableConcept = ({ concept, onClick }) => {
    const [{ isDragging }, drag] = useDrag({
        type: ItemTypes.CONCEPT,
        item: { concept },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    return (
        <li
            // 1. Attach the drag source reference
            ref={drag}

            // 2. Use a standard onClick handler for the modal
            // This is generally compatible with React Dnd for short clicks.
            onClick={() => onClick(concept)}

            className="p-2 my-1 bg-gray-100 border border-gray-300 rounded cursor-pointer shadow-sm text-sm hover:bg-gray-200 transition-colors flex justify-between items-center"
            style={{ opacity: isDragging ? 0.5 : 1 }}
        >
            <span>{concept.name}</span>
            <span className="text-gray-500 text-xs"> (Click for Details)</span>
        </li>
    );
};

// --- 3. Drop Target Component ---
const AssignedConceptsDropTarget = ({ selectedChapter, assignedConcepts, onDropConcept, onRemoveConcept, conceptMap, onConceptClick }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
        accept: ItemTypes.CONCEPT,
        drop: (item) => { if (selectedChapter) onDropConcept(item.concept); },
        canDrop: () => !!selectedChapter,
        collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
    });

    let dropClassName = 'border-2 border-dashed rounded min-h-[300px] p-3 transition-colors';
    if (isOver && canDrop) {
        dropClassName += ' border-gray-600 bg-gray-100';
    } else if (canDrop) {
        dropClassName += ' border-gray-400 bg-gray-50';
    } else {
        dropClassName += ' border-gray-300 bg-white';
    }

    return (
        <div className="flex-1 p-4 bg-white shadow-md">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">✅ Assigned Concepts</h3>
            <p className="text-sm text-gray-500 mb-3">Chapter: **{selectedChapter?.name || 'Please Select a Chapter'}**</p>

            <div ref={drop} className={dropClassName}>
                {!selectedChapter && (
                    <p className="text-center text-gray-400 mt-10">Select a chapter to enable concept assignment.</p>
                )}

                <ul className="space-y-2">
                    {assignedConcepts.map(cc => {
                        const concept = conceptMap[cc.concept?.id];
                        const conceptName = concept?.name ?? `(ID: ${cc.concept?.id} - Name Missing)`;

                        return (
                            <li
                                key={cc.id}
                                className="flex justify-between items-center p-2 bg-gray-100 border border-gray-300 rounded text-sm cursor-pointer hover:bg-gray-200"
                            >
                                <span onClick={() => onConceptClick(concept)} className="flex-1">
                                    {conceptName}
                                    <span className="text-gray-500 text-xs ml-2"> (Click for Details)</span>
                                </span>
                                <button
                                    className="text-gray-600 hover:text-gray-900 font-bold ml-4 text-xs"
                                    onClick={() => onRemoveConcept(cc.id)}
                                    title="Remove Assignment"
                                >
                                    X
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};



// --- 4. MAIN COMPONENT (State & Logic) ---

const ChapterConceptAssignment = () => {
    // Data States
    const [allMasterChapters, setAllMasterChapters] = useState([]);
    const [allConcepts, setAllConcepts] = useState([]);
    const [classStandards, setClassStandards] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [allMasterConceptChapters, setAllMasterConceptChapters] = useState([]);

    // Selection & Assignment States
    const [selectedChapter, setSelectedChapter] = useState(null);

    // Filter/Search States
    const [classStandardFilter, setClassStandardFilter] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [conceptSearchTerm, setConceptSearchTerm] = useState('');

    // UI/Error States
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false); // Concept Creation Modal

    // NEW Detail States
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedConceptForDetails, setSelectedConceptForDetails] = useState(null);

    // Derived State 1: Create a Concept Map for quick ID lookup
    const conceptMap = useMemo(() => {
        return allConcepts.reduce((map, concept) => {
            map[concept.id] = concept;
            return map;
        }, {});
    }, [allConcepts]);

    // Derived State 2: Client-Side Chapter Filtering
    const filteredChapters = useMemo(() => {
        let chapters = allMasterChapters;

        if (classStandardFilter) {
            const filterId = parseInt(classStandardFilter, 10);
            chapters = chapters.filter(c =>
                c.classStandard?.id === filterId
            );
        }

        if (subjectFilter) {
            const filterId = parseInt(subjectFilter, 10);
            chapters = chapters.filter(c =>
                c.subject?.id === filterId
            );
        }

        return chapters;

    }, [allMasterChapters, classStandardFilter, subjectFilter]);

    // Derived State 3: Client-Side Assigned Concepts Filtering
    const assignedConceptChapters = useMemo(() => {
        if (!selectedChapter) return [];

        const chapterId = selectedChapter.id;

        return allMasterConceptChapters.filter(cc =>
            cc.chapter?.id === chapterId
        );

    }, [allMasterConceptChapters, selectedChapter]);

    // Derived State 4: Unassigned Concepts
    const assignedConceptIds = assignedConceptChapters.map(cc => cc.concept?.id).filter(Boolean);
    const unassignedConcepts = allConcepts.filter(
        c => !assignedConceptIds.includes(c.id)
    );

    // --- API Call Functions (UseCallback) ---

    const fetchInitialData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [standardsRes, subjectsRes, conceptsRes, chaptersRes, conceptChaptersRes] = await Promise.all([
                apiService.getAllClassStandards(),
                apiService.getAllSubjects(),
                apiService.getAllConcepts(),
                apiService.getAllChapters(),
                apiService.getAllConceptChapters(),
            ]);

            setClassStandards(standardsRes.data);
            setSubjects(subjectsRes.data);
            setAllConcepts(conceptsRes.data);
            setAllMasterChapters(chaptersRes.data);
            setAllMasterConceptChapters(conceptChaptersRes.data);

            console.log("✅ Initial data loaded successfully.");

        } catch (err) {
            console.error("❌ API Error: Failed to fetch initial data.", err.response || err);
            setError('❌ Failed to fetch initial data. Check network tab (F12) for detailed API errors.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // --- Effects ---

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // --- Handlers ---

    const handleClassStandardChange = (e) => {
        setClassStandardFilter(e.target.value);
        setSelectedChapter(null);
    };

    const handleSubjectChange = (e) => {
        setSubjectFilter(e.target.value);
        setSelectedChapter(null);
    };

    const handleSelectChapter = (chapter) => {
        setSelectedChapter(chapter);
    };

    // NEW Handler: Open Concept Details Modal
    const handleConceptClick = useCallback((concept) => {
        if (concept) {
            setSelectedConceptForDetails(concept);
            setIsDetailsModalOpen(true);
        }
    }, []);

    // NEW Handler: Close Concept Details Modal
    const handleCloseDetailsModal = useCallback(() => {
        setIsDetailsModalOpen(false);
        setSelectedConceptForDetails(null);
    }, []);


    const assignConceptToChapter = async (concept) => {
        if (!selectedChapter || assignedConceptIds.includes(concept.id)) return;
        try {
            const response = await apiService.linkConceptChapter(selectedChapter.id, concept.id);
            const newConceptChapter = response.data;

            setAllMasterConceptChapters(prev => [...prev, newConceptChapter]);
        } catch (err) {
            console.error("❌ API Error: Failed to link concept.", err.response || err);
            setError(`❌ Failed to assign concept ${concept.name}. Check API response.`);
        }
    };

    const removeConceptFromChapter = async (conceptChapterId) => {
        if (!selectedChapter) return;
        try {
            await apiService.unlinkConceptChapter(conceptChapterId);

            setAllMasterConceptChapters(prev => prev.filter(cc => cc.id !== conceptChapterId));
        } catch (err) {
            console.error("❌ API Error: Failed to unlink concept.", err.response || err);
            setError('❌ Failed to remove concept assignment.');
        }
    };

    const createNewConcept = async (code, name, description) => {
        setError(null);
        try {
            const newConcept = { code, name, description, isActive: true };
            const response = await apiService.createConcept(newConcept);
            const createdConcept = response.data;

            setAllConcepts(prev => [...prev, createdConcept]);

            if (selectedChapter) {
                await assignConceptToChapter(createdConcept);
            }
        } catch (err) {
            console.error("❌ API Error: Failed to create new concept.", err.response || err);
            setError(`❌ Failed to create new concept. Ensure Code and Name are unique. ${err.response?.data?.title || ''}`);
        }
    };

    const filteredUnassignedConcepts = useMemo(() => unassignedConcepts.filter(c =>
        c.name.toLowerCase().includes(conceptSearchTerm.toLowerCase()) ||
        c.code.toLowerCase().includes(conceptSearchTerm.toLowerCase())
    ), [unassignedConcepts, conceptSearchTerm]);

    return (
        <Layout>
            <DndProvider backend={HTML5Backend}>
                <div className="p-2 bg-gray-50 min-h-screen">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">🔗 Chapter-Concept Assignment</h1>

                    {error && <div className="p-3 mb-4 bg-gray-100 border border-gray-500 text-gray-800 rounded shadow-md">{error}</div>}

                    {/* --- Filter Bar --- */}
                    <div className="flex space-x-4 mb-6 p-4 bg-white rounded shadow-sm border border-gray-200">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Class Standard:</label>
                            <select
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-gray-500 focus:border-gray-500"
                                value={classStandardFilter}
                                onChange={handleClassStandardChange}
                            >
                                <option value="">-- Select Standard --</option>
                                {classStandards.map(cs => (
                                    <option key={cs.id} value={cs.id}>{cs.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Subject:</label>
                            <select
                                className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-gray-500 focus:border-gray-500"
                                value={subjectFilter}
                                onChange={handleSubjectChange}
                            >
                                <option value="">-- Select Subject --</option>
                                {subjects.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* --- Three Column View --- */}
                    <div className="flex space-x-6">

                        {/* Column 1: Chapters List */}
                        <div className="flex-1 p-4 bg-white shadow-md border-r border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-700 mb-4">📚 Chapters (Select a Chapter)</h3>
                            <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
                                {isLoading && <p className="text-center text-gray-500">Loading chapters...</p>}

                                {(classStandardFilter || subjectFilter) && filteredChapters.length === 0 && !isLoading && (
                                    <p className="text-center text-gray-400 mt-5">No chapters found for the selected combination.</p>
                                )}
                                {(!classStandardFilter && !subjectFilter) && filteredChapters.length > 0 && (
                                    <p className="text-center text-gray-400 mt-5">Showing **All Chapters** (Select filters to refine).</p>
                                )}

                                <ul className="space-y-1">
                                    {filteredChapters.map(chapter => (
                                        <li
                                            key={chapter.id}
                                            className={`p-3 rounded cursor-pointer text-sm transition-colors ${selectedChapter?.id === chapter.id ? 'bg-gray-800 text-white font-bold shadow-lg' : 'bg-gray-100 hover:bg-gray-200'}`}
                                            onClick={() => handleSelectChapter(chapter)}
                                        >
                                            {chapter.name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Column 2: Assigned Concepts (Drop Target) */}
                        <AssignedConceptsDropTarget
                            selectedChapter={selectedChapter}
                            assignedConcepts={assignedConceptChapters}
                            onDropConcept={assignConceptToChapter}
                            onRemoveConcept={removeConceptFromChapter}
                            conceptMap={conceptMap}
                            onConceptClick={handleConceptClick}
                        />

                        {/* Column 3: Unassigned Concepts (Draggable Source) */}
                        <div className="flex-1 p-4 bg-white shadow-md">
                            <h3 className="text-xl font-semibold text-gray-700 mb-4">💡 Unassigned Concepts</h3>

                            {/* Search and Create */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-md p-2 mb-2 focus:ring-gray-500 focus:border-gray-500"
                                    placeholder="Search concepts by keyword or code..."
                                    value={conceptSearchTerm}
                                    onChange={(e) => setConceptSearchTerm(e.target.value)}
                                />
                                <button
                                    className="w-full bg-gray-700 hover:bg-gray-900 text-white font-bold py-2 rounded transition-colors"
                                    onClick={() => setIsModalOpen(true)} // Open Creation Modal
                                >
                                    + Create New Concept
                                </button>
                            </div>

                            {/* Draggable Concepts List Container */}
                            <div className="border border-gray-200 rounded p-1" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <ul className="space-y-2">
                                    {filteredUnassignedConcepts.map(concept => (
                                        <DraggableConcept
                                            key={concept.id}
                                            concept={concept}
                                            onClick={handleConceptClick} // PASS HANDLER
                                        />
                                    ))}
                                    {filteredUnassignedConcepts.length === 0 && <p className="text-center text-gray-400 mt-5 text-sm">No active concepts found matching your search.</p>}
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Concept Creation Modal */}
                <ConceptCreationModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onCreate={createNewConcept}
                />

                {/* NEW: Concept Details Modal */}
                <ConceptDetailsModal
                    isOpen={isDetailsModalOpen}
                    onClose={handleCloseDetailsModal}
                    concept={JSON.stringify(selectedConceptForDetails)}
                />
            </DndProvider>
        </Layout>
    );
};

export default ChapterConceptAssignment;